import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ProcessStepAssignment } from '@/api/queries/useTasks';

interface UseAutoOpenAssignmentParams {
  assignmentId: string | undefined;
  assignments: ProcessStepAssignment[] | undefined;
  selectedAssignment: ProcessStepAssignment | null;
  setSelectedAssignment: (assignment: ProcessStepAssignment | null) => void;
  slug: string;
  search: Record<string, unknown>;
  navigate: ReturnType<typeof useNavigate>;
}

export function useAutoOpenAssignment(params: UseAutoOpenAssignmentParams) {
  const {
    assignmentId,
    assignments,
    selectedAssignment,
    setSelectedAssignment,
    slug,
    search,
    navigate
  } = params;

  useEffect(
    function openAssignmentFromUrlParam() {
      if (!assignmentId || !assignments || selectedAssignment) {
        return;
      }

      const assignment = assignments.find((a) => a._id === assignmentId);
      if (!assignment) {
        return;
      }

      setSelectedAssignment(assignment);
      // Clear the URL param
      navigate({
        to: '/$slug/my-tasks',
        params: { slug },
        search: { ...search, assignmentId: undefined },
        replace: true
      });
    },
    [assignmentId, assignments, selectedAssignment, navigate, slug, search, setSelectedAssignment]
  );
}

interface UseAutoOpenSurveyParams {
  surveyId: string | undefined;
  selectedSurveyId: string | null;
  setSelectedSurveyId: (surveyId: string | null) => void;
  slug: string;
  search: Record<string, unknown>;
  navigate: ReturnType<typeof useNavigate>;
}

export function useAutoOpenSurvey(params: UseAutoOpenSurveyParams) {
  const { surveyId, selectedSurveyId, setSelectedSurveyId, slug, search, navigate } = params;

  useEffect(
    function openSurveyFromUrlParam() {
      if (!surveyId || selectedSurveyId) {
        return;
      }

      setSelectedSurveyId(surveyId);
      // Clear the URL param
      navigate({
        to: '/$slug/my-tasks',
        params: { slug },
        search: { ...search, surveyId: undefined },
        replace: true
      });
    },
    [surveyId, selectedSurveyId, navigate, slug, search, setSelectedSurveyId]
  );
}
